import React from 'react';

import { ContentEditorContext } from '@lumapps/designer-content-editor/context/ContentEditorContext';
import { CONTENT_PROPERTIES_NAMES } from '@lumapps/designer/constants';
import { useFormContext } from '@lumapps/lumx-forms';
import { useSelector } from '@lumapps/redux/react';
import { getInstanceColorPalette } from '@lumapps/style/ducks/selectors';
import { toCompatibleLanguage } from '@lumapps/translations';
import { slugify } from '@lumapps/utils/string/slugify';
import { useWidgetsAutoTranslate } from '@lumapps/widget-base/hooks/useWidgetsAutoTranslate';
import { parseTranslatedWidgets } from '@lumapps/widget-base/utils/parseTranslatedWidgets';
import { WIDGET_CONTRIBUTION_TYPE } from '@lumapps/widget-contribution/constants';
import { WIDGET_TITLE_TYPE } from '@lumapps/widget-title/constants';
import { document } from '@lumapps/wrex/document/structured-content';
import { getFirstContentAsString } from '@lumapps/wrex/slate/utils/getFirstContentAsString';
import { Nodes } from '@lumapps/wrex/types/core';

import { getWidgetBodyTranslations } from '../utils/getWidgetBodyTranslations';
import { getWidgetsTranslatableData, type WidgetsDirtyFields } from '../utils/getWidgetsTranslatableData';

interface UseApplyWidgetTranslationsParams {
    onSuccess?: (translatedValues: Record<string, (string | undefined)[]>) => void;
}

/**
 * Hook that auto-translate the given widgetIds and update their values with the content editor form
 * WARNING: Should only be used on a content-editor context (PWE)
 */
export const useApplyWidgetTranslations = ({ onSuccess }: UseApplyWidgetTranslationsParams = {}) => {
    const { widgetPaths, isCreation } = React.useContext(ContentEditorContext);
    const formContext = useFormContext();
    const { getValues, setValue } = formContext ?? {};

    const colorPalette = useSelector(getInstanceColorPalette);

    const onTranslationUpdate = React.useCallback(
        async (translatedValues: Record<string, (string | undefined)[]>) => {
            if (!setValue) {
                return;
            }

            parseTranslatedWidgets(translatedValues, {
                colorPalette,
                onWidget: (initTargetLang, { widgetId, widgetType, headerText, bodyValue, footerText }) => {
                    const targetLang = toCompatibleLanguage(initTargetLang);
                    const widgetPathEntry = widgetPaths[widgetId];
                    if (!widgetPathEntry) {
                        return;
                    }

                    const { path } = widgetPathEntry;

                    if (headerText) {
                        setValue(`${path}.title.${targetLang}`, headerText);
                    }

                    if (bodyValue !== undefined) {
                        if (widgetType === WIDGET_TITLE_TYPE) {
                            setValue(`title.${targetLang}`, bodyValue);

                            const currentSlug = getValues?.(`${CONTENT_PROPERTIES_NAMES.URL}.${targetLang}`);
                            if ((isCreation || !currentSlug) && typeof bodyValue === 'string') {
                                setValue(`${CONTENT_PROPERTIES_NAMES.URL}.${targetLang}`, slugify(bodyValue));
                            }
                        } else {
                            const bodyKey = getWidgetBodyTranslations(widgetType, {})?.key;
                            if (bodyKey) {
                                const widgetBodyValue =
                                    widgetType === WIDGET_CONTRIBUTION_TYPE
                                        ? document.parse({
                                              children: bodyValue,
                                              title:
                                                  getValues?.(`${CONTENT_PROPERTIES_NAMES.TITLE}.${targetLang}`) || '',
                                              description: getFirstContentAsString(bodyValue as Nodes),
                                          })
                                        : bodyValue;

                                setValue(`${path}.properties.${bodyKey}.${targetLang}`, widgetBodyValue);
                            }
                        }
                    }

                    if (footerText) {
                        setValue(`${path}.properties.more.label.${targetLang}`, footerText);
                    }
                },
            });

            onSuccess?.(translatedValues);
        },
        [setValue, colorPalette, widgetPaths, getValues, isCreation, onSuccess],
    );

    const { onAutoTranslate, ...restResult } = useWidgetsAutoTranslate({ onTranslationUpdate });

    const onTranslate = React.useCallback(
        async ({
            sourceLanguage,
            targetLanguages,
            widgetIds: ids,
            dirtyFields: dirty,
        }: {
            sourceLanguage: string;
            targetLanguages: string[];
            widgetIds?: string[];
            dirtyFields?: WidgetsDirtyFields;
        }) => {
            if (!getValues) {
                throw new Error('Form context not available.');
            }

            const idsSet = ids ? new Set(ids) : null;
            const relevantPaths = idsSet
                ? Object.entries(widgetPaths).filter(([uuid]) => idsSet.has(uuid))
                : Object.entries(widgetPaths);
            const widgets = relevantPaths.map(([, { path }]) => getValues(path));
            const contentTitle = getValues(CONTENT_PROPERTIES_NAMES.TITLE);
            const data = getWidgetsTranslatableData({
                widgets,
                contentTitle,
                sourceLang: sourceLanguage,
                dirtyFields: dirty,
            });

            if (!data || Object.keys(data).length === 0) {
                throw new Error('No translatable widget data found.');
            }

            await onAutoTranslate({
                widgetsTranslatableData: data,
                sourceLanguage,
                targetLanguages,
            });
        },
        [getValues, widgetPaths, onAutoTranslate],
    );

    return { ...restResult, onTranslate };
};
